import { LeaseAgreement } from "@/types/types";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import {
  Calendar,
  DollarSign,
  FileText,
  User,
  CheckCircle,
  Clock,
} from "lucide-react";
import { formatNaira } from "@/lib/utils";
import {
  calculateLeaseProgress,
  getDaysUntilLeaseEnd,
  getDaysUntilNextPayment,
} from "@/lib/tenant-service";
import { getLeaseStatusVariant, formatLeaseStatus } from "@/lib/lease-utils";

interface LeaseDetailsCardProps {
  lease: LeaseAgreement;
  propertyTitle?: string;
  landlordName?: string;
  showProgress?: boolean;
  showSignButton?: boolean;
  onSignClick?: () => void;
  currentUserId?: string;
}

export function LeaseDetailsCard({
  lease,
  propertyTitle,
  landlordName,
  showProgress = true,
  showSignButton = false,
  onSignClick,
  currentUserId,
}: LeaseDetailsCardProps) {
  const daysUntilEnd = getDaysUntilLeaseEnd(lease);
  const daysUntilPayment = getDaysUntilNextPayment(lease);
  const leaseProgress = calculateLeaseProgress(lease);

  const isTenant = currentUserId === lease.tenantId;
  const canSign =
    isTenant && !lease.tenantSigned && lease.status === "pending_signatures";

  const formatSignatureDate = (dateString?: string) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString("en-NG", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Lease Agreement</CardTitle>
          <Badge variant={getLeaseStatusVariant(lease.status)}>
            {formatLeaseStatus(lease.status)}
          </Badge>
        </div>
        {propertyTitle && (
          <CardDescription className="text-base font-medium">
            {propertyTitle}
          </CardDescription>
        )}
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Signature Status */}
        {lease.status === "pending_signatures" && (
          <div className="space-y-3 p-4 bg-muted/50 border rounded-lg">
            <h4 className="text-sm font-medium">Signature Status</h4>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Landlord:</span>
                <Badge
                  variant={lease.landlordSigned ? "success" : "warning"}
                  className="gap-1"
                >
                  {lease.landlordSigned ? (
                    <>
                      <CheckCircle className="h-3 w-3" />
                      Signed{" "}
                      {formatSignatureDate(lease.landlordSignedAt) &&
                        `on ${formatSignatureDate(lease.landlordSignedAt)}`}
                    </>
                  ) : (
                    <>
                      <Clock className="h-3 w-3" />
                      Pending
                    </>
                  )}
                </Badge>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Tenant:</span>
                <Badge
                  variant={lease.tenantSigned ? "success" : "warning"}
                  className="gap-1"
                >
                  {lease.tenantSigned ? (
                    <>
                      <CheckCircle className="h-3 w-3" />
                      Signed{" "}
                      {formatSignatureDate(lease.tenantSignedAt) &&
                        `on ${formatSignatureDate(lease.tenantSignedAt)}`}
                    </>
                  ) : (
                    <>
                      <Clock className="h-3 w-3" />
                      Pending
                    </>
                  )}
                </Badge>
              </div>
            </div>
            {showSignButton && canSign && onSignClick && (
              <Button onClick={onSignClick} className="w-full">
                <FileText className="mr-2 h-4 w-4" />
                Sign Lease Agreement
              </Button>
            )}
          </div>
        )}

        {/* Lease Duration & Progress */}
        {showProgress && lease.status === "active" && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Lease Progress</span>
              <span className="font-medium">{leaseProgress}%</span>
            </div>
            <Progress value={leaseProgress} className="h-2" />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{new Date(lease.startDate).toLocaleDateString()}</span>
              <span>{new Date(lease.endDate).toLocaleDateString()}</span>
            </div>
            <p className="text-sm text-muted-foreground">
              {daysUntilEnd} days remaining until lease end
            </p>
          </div>
        )}

        {/* Key Information Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Monthly Rent */}
          <div className="flex items-start gap-3">
            <div className="bg-primary/10 p-2 rounded-full">
              <DollarSign className="h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Monthly Rent</p>
              <p className="text-lg font-bold">
                {formatNaira(lease.monthlyRent)}
              </p>
              {lease.status === "active" && (
                <p className="text-xs text-muted-foreground">
                  Due in {daysUntilPayment} days (Day {lease.paymentDueDay})
                </p>
              )}
            </div>
          </div>

          {/* Security Deposit */}
          <div className="flex items-start gap-3">
            <div className="bg-primary/10 p-2 rounded-full">
              <FileText className="h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Security Deposit</p>
              <p className="text-lg font-bold">
                {formatNaira(lease.securityDeposit)}
              </p>
            </div>
          </div>

          {/* Lease Duration */}
          <div className="flex items-start gap-3">
            <div className="bg-primary/10 p-2 rounded-full">
              <Calendar className="h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Lease Period</p>
              <p className="text-sm font-medium">
                {new Date(lease.startDate).toLocaleDateString()} -{" "}
                {new Date(lease.endDate).toLocaleDateString()}
              </p>
            </div>
          </div>

          {/* Landlord Info */}
          {landlordName && (
            <div className="flex items-start gap-3">
              <div className="bg-primary/10 p-2 rounded-full">
                <User className="h-4 w-4 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Landlord</p>
                <p className="text-sm font-medium">{landlordName}</p>
              </div>
            </div>
          )}
        </div>

        {/* Terms & Conditions */}
        <div className="space-y-3 pt-4 border-t">
          <h4 className="text-sm font-medium">Terms & Conditions</h4>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Pets:</span>
              <span>{lease.petsAllowed ? "Allowed" : "Not Allowed"}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Smoking:</span>
              <span>{lease.smokingAllowed ? "Allowed" : "Not Allowed"}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Subletting:</span>
              <span>{lease.sublettingAllowed ? "Allowed" : "Not Allowed"}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Max Occupants:</span>
              <span>{lease.maxOccupants}</span>
            </div>
          </div>
        </div>

        {/* Utilities Included */}
        {lease.utilitiesIncluded && lease.utilitiesIncluded.length > 0 && (
          <div className="space-y-2 pt-4 border-t">
            <h4 className="text-sm font-medium">Utilities Included</h4>
            <div className="flex flex-wrap gap-2">
              {lease.utilitiesIncluded.map((utility, index) => (
                <Badge key={index} variant="secondary">
                  {utility}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Late Fee Information */}
        {lease.lateFeeAmount > 0 && (
          <div className="bg-muted/50 border rounded-lg p-3">
            <p className="text-sm">
              <span className="font-medium">Late Fee:</span>{" "}
              {formatNaira(lease.lateFeeAmount)} after{" "}
              {lease.lateFeeGracePeriod} days grace period
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
